// Date + time pair for picking a follow-up moment, with the common presets.
// Shared by FollowUpScheduler (the modal) and LeadFormModal (the required
// field on create/edit) so the two can't drift apart on defaults or on the
// merge rule below.
//
// The merge rule is the reason this is a component and not two pickers: when
// you change the date, the time you already chose has to survive, and when
// there is no time yet it has to land on 9am rather than midnight. Getting
// that wrong schedules everybody's follow-ups for 12:00 AM.

import React from "react";
import { DatePicker, TimePicker } from "antd";
import dayjs from "dayjs";

export const DEFAULT_HOUR = 9;

export const PRESETS = [
  { label: "In 1 hour", build: () => dayjs().add(1, "hour").second(0).millisecond(0) },
  { label: "In 3 hours", build: () => dayjs().add(3, "hour").second(0).millisecond(0) },
  { label: "Tomorrow 9am", build: () => dayjs().add(1, "day").hour(DEFAULT_HOUR).minute(0).second(0).millisecond(0) },
  { label: "Next Monday 10am", build: () => dayjs().day(8).hour(10).minute(0).second(0).millisecond(0) },
];

// Matches the server-side default in followUpService.defaultFollowUpAt, so the
// date the form pre-fills is the same one the API would have chosen anyway.
export const defaultFollowUpValue = () =>
  dayjs().add(1, "day").hour(DEFAULT_HOUR).minute(0).second(0).millisecond(0);

export default function FollowUpDateTime({
  value,
  onChange,
  presets = true,
  disabled = false,
  size,
}) {
  const valid = value?.isValid?.() ? value : null;

  const pickDate = (v) => {
    if (!v) return onChange(null);
    // Keep the time already chosen; fall back to a working hour, never midnight.
    const merged = valid ? v.hour(valid.hour()).minute(valid.minute()) : v.hour(DEFAULT_HOUR).minute(0);
    onChange(merged.second(0).millisecond(0));
  };

  const pickTime = (v) => {
    if (!v) return;
    const base = valid || dayjs();
    onChange(base.hour(v.hour()).minute(v.minute()).second(0).millisecond(0));
  };

  return (
    <>
      {presets && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
          {PRESETS.map((p) => {
            // Within a minute counts as "this preset", so a preset stays lit
            // while the clock ticks past the second it was clicked.
            const active = valid ? Math.abs(p.build().valueOf() - valid.valueOf()) < 60_000 : false;
            return (
              <button
                key={p.label}
                type="button"
                disabled={disabled}
                onClick={() => onChange(p.build())}
                style={{
                  padding: "5px 11px", fontSize: 13, borderRadius: 6, lineHeight: 1.5,
                  border: `1px solid ${active ? "var(--crm-primary)" : "var(--color-border-subtle)"}`,
                  background: active ? "var(--crm-primary-soft)" : "var(--color-bg-surface)",
                  color: active ? "var(--crm-primary)" : "var(--color-text-primary)",
                  fontWeight: active ? 600 : 500,
                  cursor: disabled ? "not-allowed" : "pointer",
                }}
              >
                {p.label}
              </button>
            );
          })}
        </div>
      )}

      <div style={{ display: "flex", gap: 8 }}>
        <DatePicker
          value={valid}
          onChange={pickDate}
          format="DD MMM YYYY"
          style={{ flex: 1.4 }}
          placeholder="Date"
          allowClear={false}
          disabled={disabled}
          size={size}
        />
        <TimePicker
          value={valid}
          onChange={pickTime}
          format="h:mm A"
          use12Hours
          minuteStep={5}
          style={{ flex: 1 }}
          placeholder="Time"
          allowClear={false}
          disabled={disabled}
          size={size}
        />
      </div>
    </>
  );
}
